import * as Api from '@ViewModels';
import * as React from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { toast } from 'sonner';
import { MastheadPortalId } from '../../../models/AdminModels';
import { useSystemSurveyTemplateQuery } from '../../../queries/SurveyTemplate';
import { NavigationBreadcrumbsBar } from '../../../web/components/NavigationBreadcrumbsBar';
import { Portal } from '../../../web/components/Portal';
import { CustomSurvey } from '../../../web/containers/SurveyCreation/CustomSurvey';
import { AdminDashboardPageLayout } from '../../containers/AdminDashboardPageLayout';
import { SaveSurveyTemplateModal } from '../../containers/templates/SurveyTemplates/SaveSurveyTemplateModal';

/**
 * Admin page for editing existing survey templates.
 *
 * Loads a template's surveyDefinition, displays it in CustomSurvey editor
 * in templateMode, and allows updating the template.
 *
 * NOTE: CustomSurvey doesn't currently support loading survey definitions
 * directly (it loads from backend using survey ID). This page shows a
 * placeholder until that functionality is added.
 */
export function AdminEditSurveyTemplatePage() {
	const { id } = useParams<{ id: string }>();
	const history = useHistory();
	const [isSaveModalOpen, setIsSaveModalOpen] = React.useState(false);
	const [surveyDefinition, setSurveyDefinition] = React.useState<Api.ICustomSurvey | null>(null);

	const {
		data: templateData,
		isLoading,
		error,
	} = useSystemSurveyTemplateQuery({
		id,
		enabled: !!id,
	});

	const handleSaveAsTemplate = React.useCallback((definition: Api.ICustomSurvey) => {
		setSurveyDefinition(definition);
		setIsSaveModalOpen(true);
	}, []);

	const handleTemplateUpdated = () => {
		toast.success('Survey template updated successfully');
		history.push('/surveys');
	};

	// Hide intermediate breadcrumb segments that don't have routes
	const pathComponentNameProvider = React.useCallback((pathComponent: string): string | null => {
		const names: Record<string, string> = {
			'survey-library': 'Survey library',
			edit: 'Edit',
		};
		return names[pathComponent] ?? null;
	}, []);

	if (isLoading) {
		return (
			<AdminDashboardPageLayout>
				<div>Loading template...</div>
			</AdminDashboardPageLayout>
		);
	}

	if (error || !templateData) {
		return (
			<AdminDashboardPageLayout>
				<div>Error loading template</div>
			</AdminDashboardPageLayout>
		);
	}

	return (
		<AdminDashboardPageLayout>
			<Portal destination={MastheadPortalId}>
				<NavigationBreadcrumbsBar pathComponentNameProvider={pathComponentNameProvider} />
			</Portal>

			<CustomSurvey
				templateMode={true}
				isEditingTemplate={true}
				templateId={id}
				onSaveAsTemplate={handleSaveAsTemplate}
				initialSurveyDefinition={templateData.template.surveyDefinition}
			/>

			{surveyDefinition && templateData && (
				<SaveSurveyTemplateModal
					open={isSaveModalOpen}
					onOpenChange={setIsSaveModalOpen}
					surveyDefinition={surveyDefinition}
					onSuccess={handleTemplateUpdated}
					templateId={id}
					initialTemplateName={templateData.template.name}
					initialIndustries={Object.entries(templateData.industries)
						.filter(([_, status]) => status !== undefined && status !== null)
						.map(([industry]) => industry as Api.Industry)}
					initialIndustriesWithStatus={templateData.industries as unknown as Record<string, Api.TemplateStatus>}
				/>
			)}
		</AdminDashboardPageLayout>
	);
}
